import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useTranslation } from 'react-i18next';
import {
  getNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
} from '../api/notificationsApi';
import { LoadingState } from './LoadingState';

function formatDate(value) {
  if (!value) {
    return '';
  }

  return new Date(value).toLocaleString();
}

export function NotificationBell({ notificationsPath = '/notifications', preferencesPath = '/notification-preferences' }) {
  const { t } = useTranslation('notifications');
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const count = await getUnreadNotificationCount();
      setUnreadCount(count);
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const loadRecent = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getNotifications({ per_page: 8 });
      setItems(response.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const interval = setInterval(refreshCount, 60000);
    return () => clearInterval(interval);
  }, [refreshCount]);

  const handleOpen = async (event) => {
    setAnchorEl(event.currentTarget);
    await Promise.all([loadRecent(), refreshCount()]);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = async (notification) => {
    if (!notification.is_read) {
      await markNotificationRead(notification.id);
      setUnreadCount((count) => Math.max(0, count - 1));
    }

    handleClose();
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} aria-label={t('bell.aria_label')}>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ sx: { width: 360, maxWidth: '100%' } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('bell.title')}
          </Typography>
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ p: 2 }}>
            <LoadingState message={t('bell.loading')} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('bell.empty')}
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ py: 0 }}>
            {items.map((item) => (
              <ListItem
                key={item.id}
                component={RouterLink}
                to={notificationsPath}
                onClick={() => handleItemClick(item)}
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  bgcolor: item.is_read ? 'transparent' : 'action.hover',
                }}
              >
                <ListItemText
                  primary={item.title}
                  secondary={formatDate(item.created_at)}
                  primaryTypographyProps={{ fontWeight: item.is_read ? 400 : 600 }}
                />
              </ListItem>
            ))}
          </List>
        )}
        <Divider />
        <Box sx={{ p: 1.5, display: 'flex', gap: 1, justifyContent: 'space-between' }}>
          <Button component={RouterLink} to={notificationsPath} size="small" onClick={handleClose}>
            {t('bell.all')}
          </Button>
          <Button component={RouterLink} to={preferencesPath} size="small" onClick={handleClose}>
            {t('bell.preferences')}
          </Button>
        </Box>
      </Menu>
    </>
  );
}
